import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Loading from '../../Shared/Loading';
import { useNotification } from '../../../context/NotificationContext';
import { Candidate, City, Company, Education, EmploymentHistory, Experience, ExtractedEducation, ExtractedEmployee, State, University, UniversityCompany } from '../../../Models';
import { v4 as uuidv4 } from 'uuid';
import NewEducationModal from './NewEducationModal';
import NewExperienceModal from './NewExperienceModal';
import { useParams } from 'react-router-dom';
import useApiToken from '../../../hooks/useApiToken';
import PDFViewer from '../PdfViewer';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { addCompanies, AddEducations, addExperience, AddUniversities, GetCities, GetStates, removeEducation, removeExperience } from '../../../api/masterdata.api';
import { GetCandidate, GetCandidateCv, GetCandidateSortedEducation, GetExtractedCVData, updateCVErrors } from '../../../api/candidates.api';
import { ValidateCvCandidate } from '../../../api/ai.api';

interface ExtractedEmployeeWithIds extends ExtractedEmployee {
    employment_history: (EmploymentHistory & { id: string })[];
    education: (ExtractedEducation & { id: string })[];
}

export default function ValidateCV() {
    // Core modal states
    const token = useApiToken();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const cancelButtonRef = useRef(null);
    const { candidateId, roleId } = useParams();
    const [cities, setCities] = useState<City[]>([]);
    const [states, setState] = useState<State[]>([]);
    const [candidateEducationDetails, setCandidateEducationDetails] = useState<Education[]>([]);
    const [candidateExperienceDetails, setCandidateExperienceDetails] = useState<Experience[]>([]);


    const fetchCities = () => {
        if (!token) return;
        GetCities(token).then((res: City[]) => { setCities(res) });
    }

    const fetchStates = () => {
        if (!token) return;
        GetStates(token).then((res: State[]) => setState(res));
    };


    // Notification
    const { success, error: notifyError } = useNotification();

    const [extractedCandidate, setExtractedCandidate] = useState<ExtractedEmployeeWithIds | null>(null);

    const [extractedEducation, setExtractedEducation] = useState<(ExtractedEducation & { id: string, replacedId: string, postGrad: boolean })[]>([]);
    const [extractedExperience, setExtractedExperience] = useState<(EmploymentHistory & { id: string, replacedId: string })[]>([]);

    const [selectedEducation, setSelectedEducation] = useState<Education[]>([]);
    const [newlyAddedUniversities, setNewlyAddedUniversities] = useState<University[]>([]);
    const [prevUniversities, setPrevUniversities] = useState<University[]>([]);

    const [selectedExperience, setSelectedExperience] = useState<Experience[]>([]);
    const [newlyAddedCompanies, setNewlyAddedCompanies] = useState<Company[]>([]);
    const [prevCompanies, setPrevCompanies] = useState<Company[]>([]);

    const [currentOpenEducation, setCurrentOpenEducation] = useState<ExtractedEducation & { id: string } | null>();
    const [currentOpenExperience, setCurrentOpenExperience] = useState<EmploymentHistory & { id: string } | null>();
    const [isEducationModalOpen, setIsEducationModalOpen] = useState<boolean>(false);
    const [isExperienceModalOpen, setIsExperienceModalOpen] = useState<boolean>(false);


    const [selectedEducationIds, setSelectedEducationIds] = useState<string[]>([]);
    const [selectedExperienceIds, setSelectedExperienceIds] = useState<string[]>([]);
    const [candidateCV, setCandidateCV] = useState<string>();
    const [showCV, setShowCV] = useState<boolean>(true);
    const [fullExtractedData, setFullExtractedData] = useState<ExtractedEmployee>();
    const [error, setError] = useState<string | null>(null);
    const [educationUserErrorIndex, setEducationUserErrorIndex] = useState<number | null>(null);
    const [educationExtractedErrorIndex, setEducationExtractedErrorIndex] = useState<number | null>(null);
    const [experienceUserErrorIndex, setExperienceUserErrorIndex] = useState<number | null>(null);
    const [experienceExtractedErrorIndex, setExperienceExtractedErrorIndex] = useState<number | null>(null);
    const [candidate, setCandidate] = useState<Candidate>();
    // useEffect(() => {
    //     setOpen(isOpen);
    // }, [isOpen]);

    const [isEducationDetailsMismatch, setIsEducationDetailsMismatch] = useState<boolean>(false);
    const [isEducationPeriodMismatch, setIsEducationPeriodMismatch] = useState<boolean>(false);
    const [isExperienceDetailsMismatch, setIsExperienceDetailsMismatch] = useState<boolean>(false);
    const [isExperiencePeriodMismatch, setIsExperiencePeriodMismatch] = useState<boolean>(false);
    const [isCVDataMismatch, setIsCVDataMismatch] = useState<boolean>(false);

    useEffect(() => {
        const hasConflict = isEducationDetailsMismatch || isEducationPeriodMismatch || isExperienceDetailsMismatch || isExperiencePeriodMismatch;
        setIsCVDataMismatch(!hasConflict);
    }, [isEducationDetailsMismatch, isEducationPeriodMismatch, isExperienceDetailsMismatch, isExperiencePeriodMismatch]);
    useEffect(() => {
        if (candidateId && token) {
            fetchData();
        }
    }, [candidateId, token]);

    useEffect(() => {
        if (fullExtractedData &&
            candidateEducationDetails &&
            candidateExperienceDetails &&
            !loading && !isCVDataMismatch) {

            const result = validateData(fullExtractedData);
            if (result) {
                const error = result?.error;
                setError(error);
                const index = result?.index;
                const type = result?.type;

                if (type === "education" && index) {
                    const [userIndex, extractedIndex] = index;
                    setEducationUserErrorIndex(userIndex);
                    setEducationExtractedErrorIndex(extractedIndex);
                }

                if (type === "experience" && index) {
                    const [userIndex, extractedIndex] = index;
                    setExperienceUserErrorIndex(userIndex);
                    setExperienceExtractedErrorIndex(extractedIndex);
                }
            }
        }
    }, [fullExtractedData, candidateExperienceDetails, loading, candidateEducationDetails]);

    useEffect(() => {
        if (candidateEducationDetails && candidateExperienceDetails) {
            setSelectedEducation(candidateEducationDetails);
            setSelectedExperience(candidateExperienceDetails);
        }
    }, [candidateEducationDetails, candidateExperienceDetails])
    const fetchData = () => {
        setLoading(true);
        if (candidateId && token) {
            Promise.all([
                GetCandidate(candidateId, token),
                GetExtractedCVData(candidateId, token),
                fetchCities(),
                fetchStates(),
                GetCandidateSortedEducation(candidateId, token),
                GetCandidateCv(candidateId, token).catch(() => null),
            ])
                .then(([candidateRes, extractedRes, , , sortedEducationRes, cvBlob]: [Candidate, ExtractedEmployee, any, any, Experience[], Blob | null]) => {
                    setCandidateEducationDetails(candidateRes.education);
                    setCandidateExperienceDetails(sortedEducationRes);
                    if (cvBlob) setCandidateCV(URL.createObjectURL(cvBlob));
                    setCandidate(candidateRes);
                    setIsEducationDetailsMismatch(candidateRes.isEducationDetailsMismatch)
                    setIsEducationPeriodMismatch(candidateRes.isEducationPeriodMismatch)
                    setIsExperienceDetailsMismatch(candidateRes.isExperienceDetailsMismatch)
                    setIsExperiencePeriodMismatch(candidateRes.isExperiencePeriodMismatch)
                    setIsCVDataMismatch(!candidateRes.isCVDataMismatch)

                    const employmentWithIds = extractedRes.employment_history.map((emp: EmploymentHistory) => ({
                        ...emp,
                        id: uuidv4()
                    }));

                    const educationWithIds = extractedRes.education.map((edu: ExtractedEducation) => ({
                        ...edu,
                        id: uuidv4()
                    }));

                    const employmentWithReplaceIds = extractedRes.employment_history.map((emp: EmploymentHistory) => ({
                        ...emp,
                        id: uuidv4(),
                        replacedId: ''
                    }));

                    const educationWithReplaceIds = extractedRes.education.map((edu: ExtractedEducation) => ({
                        ...edu,
                        id: uuidv4(),
                        replacedId: '',
                        postGrad: false
                    }));

                    const modifiedRes: ExtractedEmployeeWithIds = {
                        ...extractedRes,
                        employment_history: employmentWithIds,
                        education: educationWithIds
                    };

                    setFullExtractedData(extractedRes);
                    setExtractedEducation(educationWithReplaceIds);
                    setExtractedExperience(employmentWithReplaceIds);
                    setExtractedCandidate(modifiedRes);
                })
                .catch(() => {
                    notifyError("Error", "We couldn't fetch the extracted data. Please visit the role's page and generate the data if you haven't done so already.");
                })
                .finally(() => setLoading(false));
        }
    };


    const toggleSelection = async (
        item: Education | Experience | (ExtractedEducation & { id: string }) | (EmploymentHistory & { id: string }),
        type: 'education' | 'experience'
    ) => {
        if (type === 'education') {
            const education = item as Education;
            const extractedEducation = item as ExtractedEducation & { id: string };

            const isExisting = candidateEducationDetails.some(edu => edu.id === education.id);

            if (isExisting) {
                setSelectedEducation(prev =>
                    prev.some(e => e.id === education.id)
                        ? prev.filter(e => e.id !== education.id)
                        : [...prev, education]
                );
            } else {
                const extractedExisting = selectedEducation.some(edu => edu.id === education.id);
                if (extractedExisting) {
                    setSelectedEducation(prev =>
                        prev.some(e => e.id === education.id)
                            ? prev.filter(e => e.id !== education.id)
                            : [...prev, education]
                    );
                    const isNewlyAdded = newlyAddedUniversities.some(uni => uni.id === extractedEducation.id);
                    const isPrevUniversity = prevUniversities.some(uni => uni.id === extractedEducation.id);
                    if (isNewlyAdded || isPrevUniversity) {
                        setNewlyAddedUniversities(prev => prev.filter(uni => uni.id !== extractedEducation.id));
                        setPrevUniversities(prev => prev.filter(uni => uni.id !== extractedEducation.id));
                    }
                    setSelectedEducationIds(prev =>
                        prev.includes(extractedEducation.id)
                            ? prev.filter(id => id !== extractedEducation.id)
                            : [...prev, extractedEducation.id]
                    );

                }
                else {
                    setCurrentOpenEducation(extractedEducation);
                    setIsEducationModalOpen(true);
                }
            }
        } else if (type === 'experience') {
            const experience = item as Experience;
            const extractedExperience = item as EmploymentHistory & { id: string };

            const isExisting = candidateExperienceDetails.some(exp => exp.id === experience.id);

            if (isExisting) {
                setSelectedExperience(prev =>
                    prev.some(e => e.id === experience.id)
                        ? prev.filter(e => e.id !== experience.id)
                        : [...prev, experience]
                );
            } else {
                const extractedExisting = selectedExperience.some(edu => edu.id === experience.id);
                if (extractedExisting) {
                    setSelectedExperience(prev =>
                        prev.some(e => e.id === experience.id)
                            ? prev.filter(e => e.id !== experience.id)
                            : [...prev, experience]
                    );
                    const isNewlyAdded = newlyAddedCompanies.some(comp => comp.id === extractedExperience.id);
                    const isPrevCompany = prevCompanies.some(comp => comp.id === extractedExperience.id);

                    if (isNewlyAdded || isPrevCompany) {
                        setNewlyAddedCompanies(prev => prev.filter(comp => comp.id !== extractedExperience.id));
                        setPrevCompanies(prev => prev.filter(comp => comp.id !== extractedExperience.id));
                    }
                    setSelectedExperienceIds(prev =>
                        prev.includes(experience.id)
                            ? prev.filter(id => id !== experience.id)
                            : [...prev, experience.id]
                    );
                }
                else {
                    setCurrentOpenExperience(extractedExperience);
                    setIsExperienceModalOpen(true);
                }
            }
        }
    };
    const checkEducationOverlap = () => {
        if (!selectedEducation || selectedEducation.length === 0) return false;

        const periods = selectedEducation
            .map((edu) => {
                if (!edu.period) return null;
                const parts = edu.period.split("-").map(Number);
                if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
                if (parts[0] >= parts[1]) return null;
                return { start: parts[0], end: parts[1] };
            })
            .filter((period) => period !== null) as { start: number; end: number }[];

        if (periods.length === 0) return false;

        periods.sort((a, b) => a.start - b.start);

        for (let i = 0; i < periods.length - 1; i++) {
            if (periods[i + 1].start < periods[i].end) {
                notifyError("Error", "Education periods are overlapping.");
                return true;
            }
        }
        return;
    };

    const handleUpdate = async () => {
        if (!token) return;
        if (checkEducationOverlap()) {
            return;
        }
        if (candidateId)
           await updateCVErrors(candidateId, token, isEducationDetailsMismatch, isEducationPeriodMismatch, isExperienceDetailsMismatch, isExperiencePeriodMismatch, !isCVDataMismatch)

        const req: University[] = newlyAddedUniversities.map((university: any) => ({
            id: university.id,
            name: university.name,
            url: university.url,
            isConfirmed: true,
            city: cities.find(c => c.id === university.cityId) || undefined,
            state: states.find(s => s.id === university.stateId) || undefined,
        }));
        if (req.length > 0) {
            await AddUniversities(req, token).then(async () => {
                const mergedEducation = extractedEducation.map((edu) => {
                    const matchingUniversity = req.find((uni) => uni.id === edu.id);

                    if (matchingUniversity) {
                        const [startYear, endYear] = edu.period.split(" - ");
                        return {
                            id: uuidv4(),
                            university: matchingUniversity,
                            startDate: new Date(parseInt(startYear), 6, 1).toISOString(),
                            endDate: new Date(parseInt(endYear), 6, 1).toISOString(),
                            postGrad: edu.postGrad,
                            relevant: true,
                            degree: "",
                            period: edu.period,
                            school: "",
                            fieldOfStudy: ""

                        };
                    }
                    return null;
                }).filter(Boolean);
                if (mergedEducation.length > 0 && candidateId) {
                    await AddEducations(candidateId, mergedEducation, token)
                }
            })

        }
        const removedEducationIds = candidateEducationDetails
            .filter(edu => !selectedEducation.some(selected => selected.id === edu.id))
            .map(edu => edu.id);

        if (removedEducationIds.length > 0 && candidateId) {
            await removeEducation(candidateId, removedEducationIds, token)
        }

        if (prevUniversities.length > 0) {
            const mergedEducation = extractedEducation.map((edu) => {
                const matchingUniversity = prevUniversities.find((uni) => uni.id === edu.id);

                if (matchingUniversity) {
                    const [startYear, endYear] = edu.period.split(" - ");
                    return {
                        id: uuidv4(),
                        university: matchingUniversity,
                        startDate: new Date(parseInt(startYear), 6, 1).toISOString(),
                        endDate: new Date(parseInt(endYear), 6, 1).toISOString(),
                        postGrad: edu.postGrad,
                        relevant: true,
                        degree: "",
                        period: edu.period,
                        school: "",
                        fieldOfStudy: ""

                    };
                }
                return null;
            }).filter(Boolean);
            if (mergedEducation.length > 0 && candidateId) {
                await AddEducations(candidateId, mergedEducation, token)
            }
        }
        const removedExperienceIds = candidateExperienceDetails
            .filter(edu => !selectedExperience.some(selected => selected.id === edu.id))
            .map(edu => edu.id);

        if (removedExperienceIds.length > 0 && candidateId) {
            await removeExperience(candidateId, removedExperienceIds, token)
        }
        const reqCompanies: UniversityCompany[] = newlyAddedCompanies.map((company: any) => ({
            id: company.id,
            name: company.name,
            url: company.url,
            isConfirmed: true,
            isRejected: false,
            city: cities.find(c => c.id === company.cityId) || undefined,
            state: states.find(s => s.id === company.stateId) || undefined,
        }));
        if (reqCompanies.length > 0) {
            await addCompanies(reqCompanies, token);
            const mergedExperience = extractedExperience.map(exp => {
                const matchingCompany = reqCompanies.find(company => company.id === exp.id);

                if (matchingCompany) {
                    const [startMonthYear, endMonthYear] = exp.period.split(" - ");
                    const [startMonth, startYear] = startMonthYear.split("/");
                    const [endMonth, endYear] = endMonthYear ? endMonthYear.split("/") : [null, null];
                    const startYearNum = parseInt(startYear, 10) + 2000;
                    const startMonthNum = parseInt(startMonth, 10) - 1;

                    const endYearNum = endYear ? parseInt(endYear, 10) + 2000 : null;
                    const endMonthNum = endMonth ? parseInt(endMonth, 10) - 1 : null;
                    return {
                        id: uuidv4(),
                        isConfirmed: matchingCompany.isConfirmed,
                        isRejected: false,
                        name: matchingCompany.name,
                        url: matchingCompany.url,
                        title: exp.title,
                        period: exp.period,
                        description: exp.description,
                        companyName: matchingCompany.name,
                        startDate: new Date(Date.UTC(startYearNum, startMonthNum, 1)).toISOString(),
                        endDate: endYearNum !== null && endMonthNum !== null
                            ? new Date(Date.UTC(endYearNum, endMonthNum, 1)).toISOString()
                            : null,
                        relevant: true,
                        isInternship: false,
                        companyLocation: {
                            id: uuidv4(),
                            companyId: matchingCompany.id,
                            city: matchingCompany.city,
                            cityId: matchingCompany.city?.id || undefined,
                            state: matchingCompany.state,
                            isConfirmed: true,
                            isRejected: false
                        }
                    };
                }
                return null;
            }).filter(Boolean);
            if (candidateId) {
                await addExperience(mergedExperience, candidateId, token);
            }
        }
        if (prevCompanies.length > 0) {
            const mergedExperience = extractedExperience.map(exp => {
                const matchingCompany = prevCompanies.find(company => company.id === exp.id);

                if (matchingCompany) {
                    // Extract start and end dates from period
                    const [startMonthYear, endMonthYear] = exp.period.split(" - ");
                    const [startMonth, startYear] = startMonthYear.split("/");
                    const [endMonth, endYear] = endMonthYear ? endMonthYear.split("/") : [null, null];
                    const startYearNum = parseInt(startYear, 10) + 2000;
                    const startMonthNum = parseInt(startMonth, 10) - 1;

                    const endYearNum = endYear ? parseInt(endYear, 10) + 2000 : null;
                    const endMonthNum = endMonth ? parseInt(endMonth, 10) - 1 : null;
                    return {
                        id: uuidv4(),
                        isConfirmed: matchingCompany.isConfirmed,
                        isRejected: false,
                        name: matchingCompany.name,
                        url: matchingCompany.url,
                        title: exp.title,
                        period: exp.period,
                        description: exp.description,
                        companyName: matchingCompany.name,
                        startDate: new Date(Date.UTC(startYearNum, startMonthNum, 1)).toISOString(),
                        endDate: endYearNum !== null && endMonthNum !== null
                            ? new Date(Date.UTC(endYearNum, endMonthNum, 1)).toISOString()
                            : null,
                        relevant: true,
                        isInternship: false,
                        companyLocation: {
                            id: uuidv4(),
                            companyId: matchingCompany.id,
                            city: matchingCompany.city,
                            cityId: matchingCompany.city?.id || undefined,
                            state: matchingCompany.state,
                            isConfirmed: true,
                            isRejected: false
                        }
                    };
                }
                return null;
            }).filter(Boolean);
            if (candidateId) {
                addExperience(mergedExperience, candidateId, token);
            }
        }
        if(candidateId){
            await ValidateCvCandidate(candidateId, token || "");
        }
        success("Success", "Candidate Details Updated Successfully");
        setTimeout(() => {
            window.location.reload();
        }, 1000)
    };


    const onEducationAddConfirm = (formData: any, period: string, selectedEduId: string, type: "new" | "old", postGrad: boolean) => {
        if (type === "new") {
            setNewlyAddedUniversities(prev => [...prev, formData as University]);
            setSelectedEducationIds(prev =>
                prev.includes(selectedEduId)
                    ? prev.filter(id => id !== selectedEduId)
                    : [...prev, selectedEduId]
            );
            setExtractedEducation(prevEducation =>
                prevEducation.map(edu =>
                    edu.id === selectedEduId
                        ? {
                            ...edu,
                            school: formData.name,
                            city: formData.city || "",
                            url: formData.url || null,
                            degree: edu.degree,
                            period: formData.period || "",
                            fieldOfStudy: edu.fieldOfStudy || null,
                            possible_cities: formData.city ? [formData.city] : [],
                            linkedin: null,
                            postGrad: postGrad
                        }
                        : edu
                )
            );

        } else {
            setExtractedEducation(prevEducation =>
                prevEducation.map(edu =>
                    edu.id === selectedEduId
                        ? {
                            ...edu,
                            id: formData.id,
                            replacedId: selectedEduId,
                            school: formData.name,
                            city: formData.city.name || "",
                            url: formData.url || null,
                            degree: edu.degree,
                            period: period,
                            fieldOfStudy: edu.fieldOfStudy || null,
                            possible_cities: formData.city.name ? [formData.city.name] : [],
                            linkedin: formData.linkedIn,
                            postGrad: postGrad
                        }
                        : edu
                )
            );
            setPrevUniversities(prev => [...prev, formData as University]);
            setSelectedEducationIds(prev =>
                prev.includes(formData.id)
                    ? prev.filter(id => id !== formData.id)
                    : [...prev, formData.id]
            );
        }

        setIsEducationModalOpen(false);
        setSelectedEducation(prev =>
            prev.some(e => e.id === selectedEduId)
                ? prev.filter(e => e.id !== selectedEduId)
                : [...prev, formData as Education]
        );
    };

    const onExperienceAddConfirm = (formData: any, period: string, selectedExpId: string, type: "new" | "old") => {
        if (type === "new") {
            setNewlyAddedCompanies(prev => [...prev, formData as Company]);
            setSelectedExperienceIds(prev =>
                prev.includes(selectedExpId)
                    ? prev.filter(id => id !== selectedExpId)
                    : [...prev, selectedExpId]
            );
            setExtractedExperience(prevExperience =>
                prevExperience.map(exp =>
                    exp.id === selectedExpId
                        ? {
                            ...exp,
                            company: formData.name,
                            city: formData.city,
                            url: formData.url,
                            title: formData.title,
                            period: formData.period,
                            description: formData.description,
                            possible_cities: formData.city ? [formData.city] : [],
                            linkedin: null
                        }
                        : exp
                )
            );

        } else {
            setExtractedExperience(prevExperience =>
                prevExperience.map(exp =>
                    exp.id === selectedExpId
                        ? {
                            ...exp,
                            id: formData.id,
                            replacedId: selectedExpId,
                            company: formData.name,
                            city: formData.city.name,
                            url: formData.url,
                            title: formData.name,
                            period: period,
                            possible_cities: formData.city.name ? [formData.city.name] : [],
                            linkedin: formData.linkedIn,
                        }
                        : exp
                )
            );
            setPrevCompanies(prev => [...prev, formData as Company]);
            setSelectedExperienceIds(prev =>
                prev.includes(formData.id)
                    ? prev.filter(id => id !== formData.id)
                    : [...prev, formData.id]
            );
        }

        setIsExperienceModalOpen(false);
        setSelectedExperience(prev =>
            prev.some(e => e.id === selectedExpId)
                ? prev.filter(e => e.id !== selectedExpId)
                : [...prev, formData as Experience]
        );

    };
    const onEducationAddCancel = () => {
        setIsEducationModalOpen(false);
    }
    const onExperienceAddCancel = () => {
        setIsExperienceModalOpen(false);
    }
    const standardizePeriod = (period: string | undefined): string | undefined => {
        return period?.replace(/\s+/g, '').replace(/[-‐‑‒–—―]/g, '-');
    };

    const extractYears = (period: string | undefined): string | undefined => {
        return period?.replace(/[^0-9\/\-]/g, '').trim();
    };

    const getEndYear = (period: string | undefined): string | undefined => {
        if (!period) return undefined;
        const standardized = standardizePeriod(period) ?? '';
        if (!standardized.includes('-')) return standardized;
        return standardized.split('-')[1];
    };


    const validateData = (res: ExtractedEmployee) => {
        let errorIndex: [number, number] | null = null;

        if (!candidateEducationDetails && res.education.length > 0) {
            return { error: "Please provide your education details in the CV.", index: null, type: "education" };
        }
        if (res.education.length !== candidateEducationDetails.length) {
            const missingFrom = candidateEducationDetails && res.education.length > candidateEducationDetails?.length ? "CV" : "education state";
            return { error: `Education details mismatch. Some entries are missing from the ${missingFrom}.`, index: null, type: "education" };
        }

        for (let i = 0; i < res.education.length; i++) {
            const educationEndYear = getEndYear(extractYears(candidateEducationDetails[i].period));
            const cvEducationEndYear = getEndYear(extractYears(res.education[i].period));
            if (educationEndYear !== cvEducationEndYear) {
                errorIndex = [i, i];
                return {
                    error: `Education period mismatch. Expected ${candidateEducationDetails[i].period} but found ${res.education[i].period}`,
                    index: errorIndex,
                    type: "education"
                };
            }
        }
        if (candidateExperienceDetails && res.employment_history.length > 0) {
            if (res.employment_history.length !== candidateExperienceDetails?.length) {
                return { error: "Experience details mismatch. The number of work experiences does not match.", index: null, type: "experience" };
            }

            for (let i = 0; i < res.employment_history.length; i++) {
                const userIndex = i;
                const experiencePeriod = standardizePeriod(extractYears(candidateExperienceDetails[i].period));
                const cvExperiencePeriod = standardizePeriod(
                    extractYears(res.employment_history[i].period)
                );
                if (experiencePeriod !== cvExperiencePeriod) {
                    errorIndex = [userIndex, i];
                    return {
                        error: `Experience period mismatch. Expected ${candidateExperienceDetails[userIndex].period} but found ${res.employment_history[i].period}`,
                        index: errorIndex,
                        type: "experience"
                    };
                }
            }
        }
        return null;
    };
    const handleCheckboxChange = (setter: React.Dispatch<React.SetStateAction<boolean>>, value: boolean) => {
        setIsCVDataMismatch(false); // Uncheck "No Conflicts" when any conflict is checked
        setter(value);
    };
    const handleNoConflictsChange = (value: boolean) => {
        if (value) {
            // If checked, remove all conflicts
            setIsEducationDetailsMismatch(false);
            setIsEducationPeriodMismatch(false);
            setIsExperienceDetailsMismatch(false);
            setIsExperiencePeriodMismatch(false);
        } else {
            // If unchecked, restore the original API values
            if (candidate) {
                setIsEducationDetailsMismatch(candidate.isEducationDetailsMismatch);
                setIsEducationPeriodMismatch(candidate.isEducationPeriodMismatch);
                setIsExperienceDetailsMismatch(candidate.isExperienceDetailsMismatch);
                setIsExperiencePeriodMismatch(candidate.isExperiencePeriodMismatch);
            }
        }
        setIsCVDataMismatch(value);
    };
    const handleUpdateError = async () =>{
        if (!token) return;
        if (candidateId){
           await updateCVErrors(candidateId, token, isEducationDetailsMismatch, isEducationPeriodMismatch, isExperienceDetailsMismatch, isExperiencePeriodMismatch, !isCVDataMismatch)
        }
        success("Success", "Candidate Details Updated Successfully");
        setTimeout(() => {
            window.location.reload();
        }, 1000)
    }
    return (
        <div>
            <header className="bg-gray-100 py-8 dark:bg-darkbglight">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">
                    <div className="min-w-0 flex-1">
                        <nav className="flex" aria-label="Breadcrumb">
                            <ol className="flex items-center space-x-4 text-xs">
                                <li>
                                    <div>
                                        <Link
                                            to="/"
                                            className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                        >
                                            Roles
                                        </Link>
                                    </div>

                                </li>
                                <li>
                                    <div className="flex items-center">
                                        <ChevronRightIcon
                                            className="h-5 w-5 mr-4 flex-shrink-0 text-gray-400 dark:text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <span className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => navigate(-1)}>
                                            Candidates
                                        </span>
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-center">
                                        <ChevronRightIcon
                                            className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <span className="ml-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                                            Validate CV
                                        </span>
                                    </div>
                                </li>
                            </ol>
                        </nav>
                        <div className="min-w-0 flex-1">
                            <div className='flex items-center justify-between mb-4'>
                                <h1 className="mt-2 text-3xl font-bold leading-7 text-gray-900 sm:text-4xl sm:tracking-tight dark:text-gray-300">
                                    {candidate && candidate.firstName + " " + candidate.lastName}
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <div className="flex flex-col items-center justify-center w-full ">
                <div className='flex flex-row gap-9 dark:bg-darkbglight py-4 px-4 rounded-b-xl'>
                    <div className='flex flex-row items-center gap-2 dark:text-gray-300 text-gray-900'>
                        <label htmlFor="isEducationDetailsMismatch">Education Error:</label>
                        <input
                            type="checkbox"
                            id="isEducationDetailsMismatch"
                            checked={isEducationDetailsMismatch}
                            onChange={(e) => handleCheckboxChange(setIsEducationDetailsMismatch, e.target.checked)}
                        />
                    </div>
                    <div className='flex flex-row items-center gap-2 dark:text-gray-300 text-gray-900'>
                        <label htmlFor="isEducationPeriodMismatch">Education Period Mismatch:</label>
                        <input
                            type="checkbox"
                            id="isEducationPeriodMismatch"
                            checked={isEducationPeriodMismatch}
                            onChange={(e) => handleCheckboxChange(setIsEducationPeriodMismatch, e.target.checked)}
                        />
                    </div>
                    <div className='flex flex-row items-center gap-2 dark:text-gray-300 text-gray-900'>
                        <label htmlFor="isExperienceDetailsMismatch">Exp. Error:</label>
                        <input
                            type="checkbox"
                            id="isExperienceDetailsMismatch"
                            checked={isExperienceDetailsMismatch}
                            onChange={(e) => handleCheckboxChange(setIsExperienceDetailsMismatch, e.target.checked)}
                        />
                    </div>
                    <div className='flex flex-row items-center gap-2 dark:text-gray-300 text-gray-900'>
                        <label htmlFor="isExperiencePeriodMismatch">Exp. Period Mismatch:</label>
                        <input
                            type="checkbox"
                            id="isExperiencePeriodMismatch"
                            checked={isExperiencePeriodMismatch}
                            onChange={(e) => handleCheckboxChange(setIsExperiencePeriodMismatch, e.target.checked)}
                        />
                    </div>
                    <div className='flex flex-row items-center gap-2 dark:text-gray-300 text-gray-900'>
                        <label htmlFor="isCVDataMismatch">Set No Conflicts:</label>
                        <input
                            type="checkbox"
                            id="isCVDataMismatch"
                            checked={isCVDataMismatch}
                            onChange={(e) => handleNoConflictsChange(e.target.checked)}
                        />
                    </div>
                    <button className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors' onClick= {handleUpdateError}> Update </button>
                </div>
                <div className="overflow-y-auto w-full flex flex-row">
                    {loading ? <div className='flex flex-row items-center m-auto'>
                        <Loading /> 
                    </div> : 
                    <> 
                        <div className='flex flex-row p-6 w-[85%]'>
                            <div className='w-1/2 p-4'>
                                <div className='text-gray-800 dark:text-gray-300 text-center font-semibold mb-4'>User Input Data</div>
                                <div className='grid gap-6 p-4'>
                                    <div>
                                        <h3 className='text-gray-800 dark:text-gray-300 text-sm font-bold mb-3'>Education</h3>
                                        <div className='space-y-3'>
                                            {candidateEducationDetails.map((edu, i: number) => (
                                                <div key={edu.id} className={`bg-white dark:bg-darkbglight rounded-lg shadow-sm ${educationUserErrorIndex === i ? "border border-red-500" : "border border-gray-600"}`}>
                                                    <label className='flex  p-4 cursor-pointer hover:bg-gray-200 rounded-lg dark:hover:bg-gray-800 '>
                                                        <input
                                                            type="checkbox"
                                                            className='mt-1 h-4 w-4 text-blue-600'
                                                            checked={selectedEducation.some(e => e.id === edu.id)}
                                                            onChange={() => toggleSelection(edu, 'education')}
                                                        />
                                                        <div className='ml-3'>
                                                            <p className='font-medium text-gray-800 dark:text-gray-300'>{edu.university?.name}</p>
                                                            <p className='text-sm text-gray-600 dark:text-gray-400'>{edu.postGrad ? 'Post Graduate' : 'Under Graduate'}</p>
                                                            <p className='text-sm text-gray-500 dark:text-gray-500'>{edu.period}</p>
                                                        </div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className='text-gray-800 dark:text-gray-300 text-sm font-bold mb-3'>Experience</h3>
                                        <div className='space-y-3'>
                                            {candidateExperienceDetails.map((exp, i: number) => (
                                                <div key={exp.id} className={`bg-white dark:bg-darkbglight rounded-lg shadow-sm experience ${experienceUserErrorIndex === i ? "border border-red-500" : "border border-gray-600"}`}>
                                                    <label className='flex items-start p-4 cursor-pointer hover:bg-gray-200 rounded-lg dark:hover:bg-gray-800'>
                                                        <input
                                                            type="checkbox"
                                                            className='mt-1 h-4 w-4 text-blue-600'
                                                            checked={selectedExperience.some(e => e.id === exp.id)}
                                                            onChange={() => toggleSelection(exp, 'experience')}
                                                        />
                                                        <div className='ml-3'>
                                                            <p className='font-medium text-gray-800 dark:text-gray-300'>{exp?.company?.name}</p>
                                                            <p className='text-sm text-gray-600 dark:text-gray-400'>{exp.title}</p>
                                                            <p className='text-sm text-gray-500 dark:text-gray-500'>{exp.period}</p>
                                                        </div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='w-1/2 p-4'>
                                <div className='text-gray-800 dark:text-gray-300 text-center font-semibold mb-4'>Data Extracted From the CV</div>
                                <div className='grid gap-6 p-4'>
                                    <div>
                                        <h3 className='text-gray-800 dark:text-gray-300 text-sm font-bold mb-3'>Education</h3>
                                        <div className='space-y-3'>
                                            {extractedEducation.map((edu, i: number) => (
                                                <div key={edu.id} className={`bg-white dark:bg-darkbglight rounded-lg shadow-sm ${educationExtractedErrorIndex === i ? "border border-red-500" : "border border-gray-600"}`}>
                                                    <label className='flex items-start p-4 cursor-pointer hover:bg-gray-200 rounded-lg dark:hover:bg-gray-800'>
                                                        <input
                                                            type="checkbox"
                                                            className='mt-1 h-4 w-4 text-blue-600'
                                                            checked={selectedEducation.some(e => e.id === edu.id)}
                                                            onChange={() => toggleSelection(edu, 'education')}
                                                        />
                                                        <div className='ml-3'>
                                                            <p className='font-medium text-gray-800 dark:text-gray-300'>{edu.school}</p>
                                                            <p className='text-sm text-gray-600 dark:text-gray-400'>{edu.degree}</p>
                                                            <p className='text-sm text-gray-500 dark:text-gray-500'>{edu.period}</p>
                                                            {
                                                                edu.city && edu.city.length > 0 ?
                                                                    <p className='text-sm text-gray-500 dark:text-gray-300'>
                                                                        {edu.city}
                                                                    </p>
                                                                    :
                                                                    <p className='text-sm text-gray-500 dark:text-gray-300'>
                                                                        {edu.possible_cities?.length > 0 &&
                                                                            <p className='text-xs text-gray-500 dark:text-gray-300 mt-1'>
                                                                                Possible cities: {edu.possible_cities.join(', ')}
                                                                            </p>
                                                                        }
                                                                    </p>
                                                            }
                                                        </div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className='text-gray-800 dark:text-gray-300 text-sm font-bold mb-3'>Experience</h3>
                                        <div className='space-y-3'>
                                            {extractedExperience.map((exp, i: number) => (
                                                <div key={exp.id} className={`bg-white dark:bg-darkbglight rounded-lg shadow-sm experience ${experienceExtractedErrorIndex === i ? "border border-red-500" : "border border-gray-600"}`}>
                                                    <label className='flex items-start p-4 cursor-pointer hover:bg-gray-200 rounded-lg dark:hover:bg-gray-800'>
                                                        <input
                                                            type="checkbox"
                                                            className='mt-1 h-4 w-4 text-blue-600'
                                                            checked={selectedExperience.some(e => e.id === exp.id)}
                                                            onChange={() => toggleSelection(exp, 'experience')}
                                                        />
                                                        <div className='ml-3'>
                                                            <p className='font-medium text-gray-800 dark:text-gray-300'>{exp.company}</p>
                                                            <p className='text-sm text-gray-600 dark:text-gray-400'>{exp.title}</p>
                                                            <p className='text-sm text-gray-500 dark:text-gray-500'>{exp.period}</p>
                                                            {
                                                                exp.city && exp.city.length > 0 ?
                                                                    <p className='text-sm text-gray-500 dark:text-gray-300'>
                                                                        {exp.city}
                                                                    </p>
                                                                    :
                                                                    <p className='text-sm text-gray-500 dark:text-gray-500'>
                                                                        {exp.possible_cities?.length > 0 &&
                                                                            <p className='text-xs text-gray-500 dark:text-gray-300 mt-1'>
                                                                                Possible cities: {exp.possible_cities.join(', ')}
                                                                            </p>
                                                                        }
                                                                    </p>
                                                            }
                                                        </div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                         <div className='flex flex-col items-center justify-center w-[15%] gap-5 pr-10'>
                         {error && <div className=' text-red-800 dark:text-red-600 p-4 rounded-lg'>{error}</div>}
                         <button
                             onClick={handleUpdate}
                             className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors'
                         >
                             Update Candidate Details
                         </button>
                         <button
                             onClick={() => setShowCV(!showCV)}
                             className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                         >
                             {showCV ? "Hide CV" : "Show CV"}
                         </button>
                     </div>
                     </>
                    }
                       
                    <NewEducationModal education={currentOpenEducation} isModalConfirm={onEducationAddConfirm} isOpen={isEducationModalOpen} onCancel={onEducationAddCancel} size='lg' cities={cities} states={states} />
                    <NewExperienceModal experience={currentOpenExperience} isModalConfirm={onExperienceAddConfirm} isOpen={isExperienceModalOpen} onCancel={onExperienceAddCancel} size='lg' cities={cities} states={states} />



                </div>
                {showCV && candidateCV && <PDFViewer cvData={candidateCV} />}
            </div>
        </div>


    );
}
